import React, { useState, useEffect } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { useApp } from "../../context/AppContext";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useThemeColors } from "../../hooks/useThemeColors";
import { getConfigs } from "../../services/supabaseService";

function CardsIndicadores({ mesSelecionado, anoSelecionado }) {
  const { getMetaPorAno, calcularVGVTotal, calcularVGVMes } = useApp();
  const colors = useThemeColors();

  const [configMeta, setConfigMeta] = useState(null);

  // Função auxiliar para converter string formatada (ex: "R$ 30.000.000,00") ou número em Float numérico
  const converterStringParaNumero = (valor) => {
    if (typeof valor === 'number') return valor;
    if (!valor || typeof valor !== 'string') return 0;
    const apenasNumeros = valor.replace(/\D/g, '');
    if (apenasNumeros === '') return 0;
    return parseFloat(apenasNumeros) / 100;
  };

  // Carregar detalhamento da periodicidade salva no Supabase para o ano selecionado
  const loadPeriodicityConfig = async () => {
    try {
      const chaves = [
        `meta_tipo_${anoSelecionado}`,
        `meta_${anoSelecionado}_s1`,
        `meta_${anoSelecionado}_s2`,
        `meta_${anoSelecionado}_q1`,
        `meta_${anoSelecionado}_q2`,
        `meta_${anoSelecionado}_q3`,
        `meta_${anoSelecionado}_q4`,
        `meta_personalizada_${anoSelecionado}`,
      ];
      let res = {};
      try {
        res = (await getConfigs(chaves)) || {};
      } catch (errSup) {
        console.warn("Erro ao buscar no Supabase, buscando do localStorage:", errSup);
      }

      // Complementar com localStorage caso alguma chave não venha do banco
      chaves.forEach(chave => {
        if (!res[chave]) {
          try {
            const cached = localStorage.getItem(`config_${chave}`);
            if (cached) res[chave] = cached;
          } catch (e) { }
        }
      });

      setConfigMeta(res);
    } catch (e) {
      console.warn("Erro ao carregar configuração de periodicidade:", e);
    }
  };

  useEffect(() => {
    loadPeriodicityConfig();

    const handleMetaUpdated = () => {
      loadPeriodicityConfig();
    };

    window.addEventListener('metaUpdated', handleMetaUpdated);
    window.addEventListener('focus', handleMetaUpdated);

    return () => {
      window.removeEventListener('metaUpdated', handleMetaUpdated);
      window.removeEventListener('focus', handleMetaUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anoSelecionado]);

  const metaAnual = getMetaPorAno(anoSelecionado);
  const acumulado = calcularVGVTotal(anoSelecionado);
  const vgvMes =
    mesSelecionado === "Todos"
      ? acumulado
      : calcularVGVMes(anoSelecionado, mesSelecionado);

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor || 0);
  };

  const formatarValorMi = (valor) => {
    if (!valor || valor === 0) return 'R$ 0 Mi';
    if (valor >= 1000000) {
      const mi = valor / 1000000;
      const formatted = Number.isInteger(mi) ? mi.toString() : mi.toFixed(1).replace('.', ',');
      return `R$ ${formatted} Mi`;
    }
    return formatarMoeda(valor);
  };

  // Função para calcular VGV vendido dentro de um intervalo de meses
  const calcularVGVIntervalo = (ano, mesInicio, mesFim) => {
    const listaMeses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    const idxInicio = listaMeses.indexOf(mesInicio);
    const idxFim = listaMeses.indexOf(mesFim);

    if (idxInicio === -1 || idxFim === -1) {
      return acumulado;
    }

    let totalIntervalo = 0;
    const start = Math.min(idxInicio, idxFim);
    const end = Math.max(idxInicio, idxFim);

    for (let i = start; i <= end; i++) {
      totalIntervalo += calcularVGVMes(ano, listaMeses[i]);
    }
    return totalIntervalo;
  };

  // Determinar tipo de periodicidade e gerar segmentos da barra de progresso
  let tipo = configMeta?.[`meta_tipo_${anoSelecionado}`];

  if (!tipo) {
    const customStr = configMeta?.[`meta_personalizada_${anoSelecionado}`];
    if (customStr) {
      try {
        const p = JSON.parse(customStr);
        if (Array.isArray(p) && p.length > 0) tipo = 'personalizada';
      } catch (e) { }
    }
    if (!tipo && (configMeta?.[`meta_${anoSelecionado}_s1`] || configMeta?.[`meta_${anoSelecionado}_s2`])) {
      tipo = 'semestral';
    }
    if (!tipo && (configMeta?.[`meta_${anoSelecionado}_q1`] || configMeta?.[`meta_${anoSelecionado}_q2`])) {
      tipo = 'trimestral';
    }
    if (!tipo) tipo = 'anual';
  }

  let segmentos = [];

  if (tipo === 'semestral') {
    const s1Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_s1`]);
    const s2Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_s2`]);
    const s1Vgv = calcularVGVIntervalo(anoSelecionado, 'Janeiro', 'Junho');
    const s2Vgv = calcularVGVIntervalo(anoSelecionado, 'Julho', 'Dezembro');

    segmentos = [
      {
        label: '1º Semestre',
        sublabel: 'Jan - Jun',
        meta: s1Goal > 0 ? s1Goal : (metaAnual / 2),
        vgv: s1Vgv,
      },
      {
        label: '2º Semestre',
        sublabel: 'Jul - Dez',
        meta: s2Goal > 0 ? s2Goal : (metaAnual / 2),
        vgv: s2Vgv,
      },
    ];
  } else if (tipo === 'trimestral') {
    const q1Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_q1`]);
    const q2Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_q2`]);
    const q3Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_q3`]);
    const q4Goal = converterStringParaNumero(configMeta?.[`meta_${anoSelecionado}_q4`]);

    const fallbackGoal = metaAnual / 4;

    segmentos = [
      {
        label: '1º Trimestre',
        sublabel: 'Jan - Mar',
        meta: q1Goal > 0 ? q1Goal : fallbackGoal,
        vgv: calcularVGVIntervalo(anoSelecionado, 'Janeiro', 'Março'),
      },
      {
        label: '2º Trimestre',
        sublabel: 'Abr - Jun',
        meta: q2Goal > 0 ? q2Goal : fallbackGoal,
        vgv: calcularVGVIntervalo(anoSelecionado, 'Abril', 'Junho'),
      },
      {
        label: '3º Trimestre',
        sublabel: 'Jul - Set',
        meta: q3Goal > 0 ? q3Goal : fallbackGoal,
        vgv: calcularVGVIntervalo(anoSelecionado, 'Julho', 'Setembro'),
      },
      {
        label: '4º Trimestre',
        sublabel: 'Out - Dez',
        meta: q4Goal > 0 ? q4Goal : fallbackGoal,
        vgv: calcularVGVIntervalo(anoSelecionado, 'Outubro', 'Dezembro'),
      },
    ];
  } else if (tipo === 'personalizada') {
    const jsonStr = configMeta?.[`meta_personalizada_${anoSelecionado}`];
    let items = [];
    if (jsonStr) {
      try {
        const parsed = JSON.parse(jsonStr);
        if (Array.isArray(parsed) && parsed.length > 0) {
          items = parsed;
        }
      } catch (e) { }
    }

    if (items.length > 0) {
      let subIndex = 1;
      items.forEach((item) => {
        const mesInicio = item.mesInicio || 'Janeiro';
        const mesFim = item.mesFim || 'Dezembro';
        const periodoStr = mesInicio === mesFim ? mesInicio : `${mesInicio} a ${mesFim}`;
        const vgvIntervalo = calcularVGVIntervalo(anoSelecionado, mesInicio, mesFim);

        const v1 = converterStringParaNumero(item.valor1 !== undefined && item.valor1 !== '' ? item.valor1 : (item.valor || 0));
        const v2 = converterStringParaNumero(item.valor2 || 0);
        const v3 = converterStringParaNumero(item.valor3 || 0);

        const subMetas = [v1, v2, v3].filter((v) => v > 0);

        if (subMetas.length > 0) {
          subMetas.forEach((val) => {
            segmentos.push({
              label: `Meta ${subIndex}`,
              sublabel: periodoStr,
              meta: val,
              vgv: vgvIntervalo,
            });
            subIndex++;
          });
        } else {
          segmentos.push({
            label: `Meta ${subIndex}`,
            sublabel: periodoStr,
            meta: 0,
            vgv: vgvIntervalo,
          });
          subIndex++;
        }
      });
    }
  }

  // Se não há segmentos específicos ou é Anual, fallback para 1 segmento único anual
  if (segmentos.length === 0) {
    segmentos = [
      {
        label: 'Meta Anual',
        sublabel: 'Ano Completo',
        meta: metaAnual,
        vgv: acumulado,
      },
    ];
  }

  // Calcular VGV total consolidado do período (sem somar sub-metas para evitar duplicidade)
  let totalVgvConsolidado = acumulado;
  if (tipo === 'personalizada' && configMeta?.[`meta_personalizada_${anoSelecionado}`]) {
    try {
      const parsed = JSON.parse(configMeta[`meta_personalizada_${anoSelecionado}`]);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const firstMes = parsed[0].mesInicio || 'Janeiro';
        const lastMes = parsed[parsed.length - 1].mesFim || 'Dezembro';
        totalVgvConsolidado = calcularVGVIntervalo(anoSelecionado, firstMes, lastMes);
      }
    } catch (e) { }
  }

  // Calcular Meta Total do Período (Soma das sub-metas configuradas ou Meta Anual)
  const totalMetaPeriodo = segmentos.reduce((sum, seg) => sum + (seg.meta || 0), 0) || metaAnual;
  const displayMetaTotal = totalMetaPeriodo > 0 ? totalMetaPeriodo : metaAnual;

  const numSegmentos = segmentos.length;

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              backgroundColor: "#1D1D1D",
              borderRadius: 3,
              p: 2.5,
              border: `1px solid ${colors.border.primary}`,
              position: "relative",
              overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Meta Total
                  </Typography>
                  {segmentos[0]?.sublabel && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.7)",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.15)",
                        px: 1,
                        py: 0.2,
                        borderRadius: "6px",
                      }}
                    >
                      {segmentos[0].sublabel}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: "text.primary",
                    fontWeight: "bold",
                    fontSize: "2.4rem",
                    lineHeight: 1.1,
                  }}
                >
                  {formatarMoeda(displayMetaTotal)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: colors.hover.medium,
                  borderRadius: 2,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrendingUpIcon sx={{ color: colors.primary, fontSize: 32 }} />
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              background: '#1D1D1D',
              borderRadius: 3,
              p: 2.5,
              border: `1px solid ${colors.border.primary}`,
              position: "relative",
              overflow: "hidden",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    mb: 0.5,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  VGV {mesSelecionado}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "text.primary",
                    fontWeight: "bold",
                    fontSize: "2.4rem",
                    lineHeight: 1.1,
                  }}
                >
                  {formatarMoeda(vgvMes)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: colors.hover.medium,
                  borderRadius: 2,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AttachMoneyIcon sx={{ color: colors.primary, fontSize: 32 }} />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Container Principal de Acompanhamento de Meta Única do Período */}
      {(() => {
        const totalMetaPeriodo = segmentos.reduce((sum, seg) => sum + (seg.meta || 0), 0) || metaAnual;
        const currentVGV = totalVgvConsolidado;
        const overallPercent = totalMetaPeriodo > 0 ? (currentVGV / totalMetaPeriodo) * 100 : 0;
        const overallPercentBounded = Math.min(100, overallPercent);
        const isTotalConcluida = overallPercent >= 100;
        // Calcular divisores e posições de sub-metas no trilho
        let acumWidth = 0;
        const subMetaDividers = segmentos.map((seg) => {
          const share = totalMetaPeriodo > 0 ? (seg.meta / totalMetaPeriodo) * 100 : (100 / numSegmentos);
          const startPct = acumWidth;
          acumWidth += share;
          return {
            ...seg,
            share,
            startPct,
            endPct: acumWidth,
          };
        });

        return (
          <Box sx={{ mt: 10, mb: 0, px: 2, position: "relative", overflow: "visible" }}>
            {/* Rótulos das Sub-Metas (Meta 1 / Meta 2) Alinhados Próximos à Barra */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: "78px",
                mr: "85px",
                mb: 0.3,
                position: "relative",
                height: 22,
              }}
            >
              {subMetaDividers.map((seg, i) => (
                <Box
                  key={`top-label-${i}`}
                  sx={{
                    position: "absolute",
                    left: `${seg.startPct}%`,
                    width: `${seg.share}%`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: "#D2B35C",
                      fontWeight: 900,
                      fontSize: "1.1rem",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {seg.label ? seg.label.toUpperCase() : `META ${i + 1}`} - {formatarValorMi(seg.meta)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Container Principal da Barra Moderna */}
            <Box sx={{ display: "flex", alignItems: "center", position: "relative", overflow: "visible" }}>
              {/* 1. Círculo com Duplo Anel (à Esquerda - Com Padding para não cortar) */}
              <Box
                sx={{
                  width: 94,
                  height: 94,
                  borderRadius: "50%",
                  backgroundColor: "#1D1D1D",
                  border: isTotalConcluida
                    ? "5px solid #4CAF50"
                    : "5px solid #D2B35C",
                  boxShadow: isTotalConcluida
                    ? "0 0 22px rgba(76, 175, 80, 0.6), inset 0 0 10px rgba(0,0,0,0.8)"
                    : "0 0 22px rgba(210, 179, 92, 0.6), inset 0 0 10px rgba(0,0,0,0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 10,
                  flexShrink: 0,
                  outline: "3px solid #141414",
                  ml: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 78,
                    height: 78,
                    borderRadius: "50%",
                    border: isTotalConcluida
                      ? "2px solid rgba(76, 175, 80, 0.7)"
                      : "2px solid rgba(210, 179, 92, 0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#161616",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: isTotalConcluida ? "#4CAF50" : "#D2B35C",
                      fontWeight: 900,
                      fontSize: "1.6rem",
                      lineHeight: 1,
                    }}
                  >
                    {overallPercent.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>

              {/* 2. O Trilho da Barra (Com Height Aumentado para 58px) */}
              <Box
                sx={{
                  flexGrow: 1,
                  height: 58,
                  borderRadius: "0 29px 29px 0",
                  backgroundColor: "#222222",
                  border: isTotalConcluida
                    ? "2px solid rgba(76, 175, 80, 0.6)"
                    : "2px solid rgba(210, 179, 92, 0.5)",
                  borderLeft: "none",
                  position: "relative",
                  overflow: "visible",
                  marginLeft: "-18px",
                  boxShadow: "inset 0 3px 8px rgba(0,0,0,0.8)",
                }}
              >
                {/* Preenchimento e Divisores com clip interno */}
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "0 27px 27px 0",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Divisores Verticais de Sub-Metas */}
                  {subMetaDividers.map((div, i) => {
                    if (i === subMetaDividers.length - 1) return null;
                    return (
                      <Box
                        key={i}
                        sx={{
                          position: "absolute",
                          left: `${div.endPct}%`,
                          top: 0,
                          bottom: 0,
                          width: "3px",
                          backgroundColor: "rgba(255, 255, 255, 0.5)",
                          zIndex: 4,
                          boxShadow: "0 0 4px rgba(0,0,0,0.9)",
                        }}
                      />
                    );
                  })}

                  {/* Preenchimento VGV Dourado / Verde com Ponta Arredondada (Radius 50px) */}
                  {overallPercentBounded > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${overallPercentBounded}%`,
                        background: isTotalConcluida
                          ? "linear-gradient(90deg, #2E7D32 0%, #4CAF50 100%)"
                          : "linear-gradient(90deg, #96762B 0%, #D2B35C 50%, #F5E6AB 100%)",
                        boxShadow: isTotalConcluida
                          ? "0 0 15px rgba(76, 175, 80, 0.6)"
                          : "0 0 15px rgba(210, 179, 92, 0.6)",
                        borderRadius: "0 50px 50px 0",
                        transition: "width 0.6s ease",
                        zIndex: 2,
                      }}
                    />
                  )}
                </Box>


              </Box>

              {/* Box de 100% à Direita */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: "80px",
                  justifyContent: "center",
                  ml: 2,
                }}
              >
                <Typography variant="h5" sx={{ color: "#D2B35C", fontWeight: 900, fontSize: "2rem" }}>
                  100%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })()}
    </Box>
  );
}

export default CardsIndicadores;
